import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFoodById, FoodDto, Units } from '../services/foods';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';

function formatUnits(units: Units): string {
  switch (units) {
    case Units.Piece: return 'ks';
    case Units.Liter: return 'l';
    case Units.Pinch: return 'špetka';
    case Units.Deciliter: return 'dl';
    case Units.Clove: return 'stroužek';
    case Units.Kilogram: return 'kg';
    case Units.Gram: return 'g';
    case Units.TableSpoon: return 'lžíce';
    case Units.TeaSpoon: return 'lžička';
    case Units.Milliliter: return 'ml';
    case Units.Handful: return 'hrst';
    case Units.Sprig: return 'snítka';
    case Units.Crucible: return 'kelímek';
    case Units.Can: return 'plechovka';
    case Units.Slice: return 'plátek';
    case Units.Volume: return 'objem';
    case Units.Stem: return 'stonek';
    case Units.Cup: return 'šálek';
    case Units.Mug: return 'hrnek';
    case Units.GlassOf: return 'sklenice';
    case Units.Cube: return 'kostka';
    case Units.Package: return 'balení';
    case Units.Bowl: return 'miska';
    case Units.Bottle: return 'láhev';
    default: return '';
  }
}

export default function FoodDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [food, setFood] = useState<FoodDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use browser history to go back (preserves query params)
  const handleGoBack = () => {
    // Check if there's history to go back to, otherwise navigate to foods
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/foods');
    }
  };

  useEffect(() => {
    const fetchFood = async () => {
      if (!id) {
        setError('No food ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getFoodById(id);
        setFood(data);
      } catch (err) {
        console.error('Error fetching food:', err);
        setError('Failed to load food details. Please make sure you are logged in.');
      } finally {
        setLoading(false);
      }
    };

    fetchFood();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={2}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          sx={{ mt: 2 }}
        >
          Zpět na recepty
        </Button>
      </Box>
    );
  }

  if (!food) {
    return (
      <Box m={2}>
        <Alert severity="info">Food not found.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          sx={{ mt: 2 }}
        >
          Zpět na recepty
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleGoBack}
        sx={{ mb: 2 }}
      >
        Zpět na recepty
      </Button>

      {/* Header section: Image + Basic Info side by side on desktop */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          mb: 3,
        }}
      >
        {/* Image */}
        {food.images && food.images.length > 0 && (
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: '100%', md: 300 },
              height: { xs: 200, md: 300 },
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={food.images[0].path}
              alt={food.name}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        )}

        {/* Basic Info */}
        <Paper sx={{ flex: 1, p: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {food.name}
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon color="action" />
              <Typography variant="body1" color="text.secondary">
                {food.durationMinutes} minut
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon color="action" />
              <Typography variant="body1" color="text.secondary">
                {food.peopleCount} {food.peopleCount === 1 ? 'osoba' : food.peopleCount < 5 ? 'osoby' : 'osob'}
              </Typography>
            </Box>
          </Box>

          {food.categoryNames && food.categoryNames.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Kategorie
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {food.categoryNames.map((category, index) => (
                  <Chip key={index} label={category} size="small" />
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Content section: Ingredients + Recipe side by side on desktop */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          alignItems: 'flex-start',
        }}
      >
        {/* Ingredients - compact list */}
        {food.ingredients && food.ingredients.length > 0 && (
          <Paper
            sx={{
              width: { xs: '100%', md: 280 },
              flexShrink: 0,
              p: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Ingredience
            </Typography>
            <List dense disablePadding>
              {food.ingredients.map((ingredient, index) => (
                <Box key={ingredient.id}>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={ingredient.ingredientName}
                      secondary={`${ingredient.quantity} ${formatUnits(ingredient.units)}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  {index < food.ingredients.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </Paper>
        )}

        {/* Recipe - takes remaining space */}
        {food.recipe && (
          <Paper sx={{ flex: 1, p: 2, minWidth: 0 }}>
            <Typography variant="h6" gutterBottom>
              Recept
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {food.recipe}
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
}
